import React from 'react';
import { ShieldCheckIcon } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { SettingsGroup, SettingsToggleRow } from '../../components/SettingsList';
import { Card, SectionTitle } from '../../components/ui/Card';
import { engagementConfig } from '../../data/engagement';
import { useEngagement } from '../../contexts/EngagementContext';
import { useLocale } from '../../contexts/LocaleContext';
type DayStatus = 'done' | 'protected' | 'none';

/** Example week: one missed day covered by protection, streak intact. */
const exampleWeek: DayStatus[] = ['done', 'done', 'done', 'protected', 'done', 'done', 'none'];
const WEEKDAYS: Record<string, string[]> = {
  'pt-BR': ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
  'en-US': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
};

/** MOB/Stats/StreakProtection — Proteção de Sequência. */
export function StreakProtection() {
  const {
    t,
    locale
  } = useLocale();
  const engagement = useEngagement();
  const days = WEEKDAYS[locale];
  const status = !engagement.protectionEnabled ? t('eng.prot.off') : engagement.protectionCharges > 0 ? t('eng.prot.available', {
    n: engagement.protectionCharges
  }) : t('eng.prot.none');
  return <div className="px-2 pb-2" data-id="element-3031" data-mp="2j4rumtf1vvvi">
      <PageHeader title={t('eng.prot.title')} subtitle={t('eng.prot.subtitle')} showBack data-id="element-3032" data-mp="2e43uni8ug4p5" />

      <Card tone="dark" className="rounded-panel p-3" data-id="element-3033" data-mp="2e6gkehkufjcr">
        <div className="flex items-center gap-2" data-id="element-3034" data-mp="2s2kco5c5phml">
          <span className="grid h-11 w-11 place-items-center rounded-[13px] border border-dashed border-gold text-gold" data-id="element-3035" data-mp="2o29w5yqqn8th">
            <ShieldCheckIcon size={20} aria-hidden="true" data-id="element-3036" data-mp="31u7wpl6dvwe1" />
          </span>
          <p className="text-[15px] font-bold text-sand" data-id="element-3037" data-mp="2wuljvcugwi5r">{status}</p>
        </div>
      </Card>

      <div className="mt-2" data-id="element-3038" data-mp="2wwy27r450ujc">
        <SettingsGroup data-id="element-3039" data-mp="3br2uu2yqvurs">
          <SettingsToggleRow label={t('eng.prot.toggle')} hint={t('eng.prot.toggleHint')} checked={engagement.protectionEnabled} onChange={engagement.setProtectionEnabled} data-id="element-3040" data-mp="3bsnqjvwspjr8" />
        </SettingsGroup>
      </div>

      <section className="mt-3" data-id="element-3041" data-mp="3iuj6qpa5r0al">
        <SectionTitle data-id="element-3042" data-mp="3ivu5ubsnrjvd">{t('eng.prot.example')}</SectionTitle>
        <Card className="p-2" data-id="element-3043" data-mp="3ntnd785p6jx8">
          <ol className="grid grid-cols-7 gap-1" data-id="element-3044" data-mp="3ntdm1h8x26py">
            {exampleWeek.map((day, index) => {
            const label = day === 'done' ? t('eng.prot.dayDone', {
              day: days[index]
            }) : day === 'protected' ? t('eng.prot.dayProtected', {
              day: days[index]
            }) : t('eng.prot.dayNone', {
              day: days[index]
            });
            return <li key={index} className="flex flex-col items-center gap-1" aria-label={label} data-id="element-3045" data-mp="ag28a99xk4gq" data-mpi={index}>
                  <span aria-hidden="true" className={`grid h-10 w-full max-w-[40px] place-items-center rounded-[10px] ${day === 'done' ? 'bg-deep text-gold' : day === 'protected' ? 'border-[1.5px] border-dashed border-gold bg-sand text-gold-deep' : 'bg-sand-deep'}`} data-id="element-3046" data-mp="fdke3qsni3ki">
                    {day === 'protected' && <ShieldCheckIcon size={15} data-id="element-3047" data-mp="1zwnx00eotiuu" />}
                  </span>
                  <span className="text-[10px] font-medium text-muted" aria-hidden="true" data-id="element-3048" data-mp="1uymf3iszzpky">
                    {days[index]}
                  </span>
                </li>;
          })}
          </ol>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-sand-line pt-2" data-id="element-3049" data-mp="2nmi7wcfg4xcg">
            {[{
            key: 'eng.prot.legend.done',
            cls: 'bg-deep'
          }, {
            key: 'eng.prot.legend.protected',
            cls: 'border border-dashed border-gold bg-sand'
          }, {
            key: 'eng.prot.legend.none',
            cls: 'bg-sand-deep'
          }].map(item => <span key={item.key} className="flex items-center gap-1 text-[11px] text-muted" data-id="element-3050" data-mp="127kvn5i9v7vm">
                <span className={`h-3 w-3 rounded-[4px] ${item.cls}`} aria-hidden="true" data-id="element-3051" data-mp="2r2jpt751vgj4" />
                {t(item.key)}
              </span>)}
          </div>
        </Card>
      </section>

      <section className="mt-3" data-id="element-3052" data-mp="2h5f6rg6i416j">
        <SectionTitle data-id="element-3053" data-mp="2h6gscel5862i">{t('eng.prot.how')}</SectionTitle>
        <ul className="space-y-2 rounded-card border border-sand-line bg-white/70 p-2" data-id="element-3054" data-mp="2m2xfb5glwee0">
          {[t('eng.prot.rule1'), t('eng.prot.rule2'), t('eng.prot.rule3', {
          n: engagementConfig.streakProtection.rechargeDays
        }), t('eng.prot.rule4', {
          n: engagementConfig.streakProtection.minStreakDays
        })].map(rule => <li key={rule} className="flex gap-2 text-[12.5px] leading-snug text-deep" data-id="element-3055" data-mp="34u82pcn4i915">
              <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-pill bg-gold" aria-hidden="true" data-id="element-3056" data-mp="34w1cv1wy3bdw" />
              {rule}
            </li>)}
        </ul>
      </section>
    </div>;
}